import safeStringify from 'fast-safe-stringify'
import { Event } from 'graphql-ws'
import { getEnv, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { AppState, NativeModules, Platform } from 'react-native'
//import { NetworkType } from 'react-native-agora'

import NetInfo, { NetInfoState, NetInfoStateType, NetInfoSubscription } from '@react-native-community/netinfo'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { withRootStore } from '../extensions'
import { RootStoreEnv } from '../RootStoreEnv'
import { WebSocketConnectionState, WebSocketEvents, webSocketEvents } from './SubscriptionClient'

const logger = extendLogger(LoggerContexts.NetworkStore)

// NetInfo.configure({
//   reachabilityUrl: 'https://clients3.google.com/generate_204',
//   reachabilityTest: async (response) => response.status === 204,
//   reachabilityLongTimeout: 60 * 1000, // 60s
//   reachabilityShortTimeout: 5 * 1000, // 5s
//   reachabilityRequestTimeout: 15 * 1000, // 15s
//   reachabilityShouldRun: () => true,
//   shouldFetchWiFiSSID: true, // met iOS requirements to get SSID. Will leak memory if set to true without meeting requirements.
//   useNativeReachability: false,
// })

const replacer = (key: string, value: unknown) => {
  // logger.log('Key:', JSON.stringify(key), 'Value:', JSON.stringify(value))
  // Remove the circular structure
  if (value === '[Circular]') {
    return
  }
  return value
}

/**
 * A NetworkStore model.
 */

export const NetworkStore = types
  .model('NetworkStore')
  .props({
    isNetworkConnected: types.maybeNull(types.boolean),
    isInternetReachable: types.maybeNull(types.boolean),
    networkType: types.optional(types.string, NetInfoStateType.unknown),
    webSocketEvents: types.optional(types.array(types.string), []),
    webSocketState: types.optional(
      types.enumeration<WebSocketConnectionState>('webSocketConnectionState', Object.values(WebSocketConnectionState)),
      WebSocketConnectionState.unknown
    ),
    isNetworkDown: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .volatile((self) => {
    const netInfoState = {
      type: NetInfoStateType.unknown,
      isConnected: null,
      isInternetReachable: null,
      details: null
    } as NetInfoState
    const eventSubscriptions: Array<() => void> = []
    return {
      eventSubscriptions,
      netInfoState
    }
  })
  .actions((self) => ({
    setIsNetworkConnected(value: boolean | null) {
      self.isNetworkConnected = value
    },
    setNetworkType(type: string) {
      self.networkType = type
    },
    setInternetReachable(value: boolean | null) {
      self.isInternetReachable = value
    },
    setWebSocketState(state: WebSocketConnectionState) {
      logger.debug('setWebSocketState', state)
      self.webSocketState = state
    },
    setIsNetworkDown(value: boolean) {
      self.isNetworkDown = value
    }
  }))
  .views((self) => ({
    get isWebSocketConnected() {
      return self.webSocketState === WebSocketConnectionState.connected
    }
  }))
  .actions((self) => ({
    setNetInfo(netInfoState: NetInfoState) {
      // logger.log('setNetInfo', netInfoState)
      // logger.log('setNetInfo isConnected', netInfoState.isConnected)
      // logger.log('setNetInfo isInternetReachable', netInfoState.isInternetReachable)
      self.netInfoState = { ...netInfoState }
      self.setNetworkType(netInfoState.type)
      self.setIsNetworkConnected(netInfoState.isConnected)
      self.setInternetReachable(netInfoState.isInternetReachable)
    },
    pushWebSocketEvent(state: Event, message?: string) {
      logger.info('pushWebSocketEvent', state)
      // setup connection listeners
      const connectionState = WebSocketConnectionState[state as keyof typeof WebSocketConnectionState]
      if (connectionState) {
        logger.debug(`webSocket.on(${connectionState}) update connection state`)
        self.setWebSocketState(connectionState)
      } else if ([WebSocketEvents.error].includes(state as WebSocketEvents)) {
        self.rootStore.messages.clearMessages()
      }
      const event = message ? `${state}${message}` : state
      self.webSocketEvents.replace([...self.webSocketEvents.slice(-20), event])
    }
  }))
  .actions((self) => {
    const teardownWebSocketListeners = () => {
      self.eventSubscriptions.map((unsubscribe) => unsubscribe?.())
    }
    const setupWebSocketListeners = () => {
      logger.debug('setupWebSocketListeners')
      teardownWebSocketListeners()
      self.eventSubscriptions = webSocketEvents.map((event) => {
        logger.debug('setupWebSocketListeners', event)
        const env = getEnv<RootStoreEnv>(self)
        const webSocketClient = env.gqlWsClient || env.graphqlWsClient
        return webSocketClient.on(event, (params: unknown) => {
          logger.debug(`webSocket.on(${event})`, params)
          let message = ''
          try {
            message = params ? ` ${safeStringify(params, replacer, 2)}` : ''
          } catch (error) {
            logger.error('error', error)
          }
          self.pushWebSocketEvent(event, message)
        })
      })
    }

    const refresh = async () => {
      const netInfoState = await NetInfo.refresh()
      logger.debug('afterAttach', netInfoState)
      self.setNetInfo(netInfoState)
    }

    // NOTE: Switching between different Wi-Fi does not send events in iOS
    // The SCNetworkReachability API used in iOS does not send events to the app in the background, so switching from one Wi-Fi network to another when your App was in background will not send an event and your network state will be out of sync with device state. To be sure you have up to date status when your app is in foreground again, you should re-fetch state each time when App comes to foreground, something like this:
    // https://github.com/react-native-netinfo/react-native-netinfo#switching-between-different-wi-fi-does-not-send-events-in-ios
    const isIOS = Platform.OS === 'ios'
    const unsubscribeAppState = AppState.addEventListener('change', async (nextAppState) => {
      logger.info('AppState changed', nextAppState)
      if (isIOS && nextAppState === 'active') {
        const netInfoState = await NativeModules.RNCNetInfo?.getCurrentState('wifi')
        // your code here
        self.setNetInfo(netInfoState)
      }
    })

    let unsubscribe: NetInfoSubscription

    const afterAttach = async () => {
      logger.debug('afterAttach')
      try {
        unsubscribe = NetInfo.addEventListener((state) => self.setNetInfo(state))
        self.setWebSocketState(WebSocketConnectionState.unknown)
        self.setIsNetworkConnected(false)
        self.setInternetReachable(false)
        // self.setNetworkType(NetworkType[NetworkType.Unknown])
        //self.setNetworkType(NetworkType[NetworkType.NetworkTypeUnknown])
        self.webSocketEvents.clear()
        // setupWebSocketListeners()
        const netInfoState = await NetInfo.fetch()
        logger.debug('afterAttach', netInfoState)
        self.setNetInfo(netInfoState)
      } catch (error) {
        logger.error(error)
      }
    }

    const beforeDestroy = () => {
      if (unsubscribe) {
        unsubscribe()
      }
      unsubscribeAppState.remove()
      self.eventSubscriptions.map((unsubscriber) => unsubscriber())
    }

    return { afterAttach, beforeDestroy, setupWebSocketListeners, teardownWebSocketListeners, refresh }
  })

/**
 * The NetworkStore instance.
 */
export type NetworkStoreType = Instance<typeof NetworkStore>

/**
 * The data of a NetworkStore.
 */
export type NetworkStoreSnapshot = SnapshotOut<typeof NetworkStore>
